import { instance as boompayClient } from "api/apiBase";
import { inboxClient } from "../../inbox/api/client";
import type {
  AssignedPropertiesPayload,
  AvailablePhoneNumber,
  BuyPhoneNumberPayload,
  ImportFromTwilioPayload,
  LeasingAgentListItem,
  PhoneNumber,
  PhoneNumbersListResponse,
  PropertyPhoneDefault,
  SearchAvailableParams,
  UpdatePhoneNumberPayload
} from "./types";

const PORTAL_BASE = "/portal/v1/provisioned_phone_numbers";

// Property-default endpoints still live on comm-service direct API
// (not exposed by boompay-api PR 4079).
const COMM_BASE = "/api/portal/provisioned_phone_numbers";
const DEFAULT_PER_PAGE = Number(
  window.localStorage.getItem("per_page") || "10"
);

export const phoneNumbersApi = {
  list: async (
    page = 1,
    perPage = DEFAULT_PER_PAGE,
    search?: string,
    propertyIds?: number[]
  ) => {
    const { data } = await boompayClient.get<PhoneNumbersListResponse>(
      PORTAL_BASE,
      {
        params: {
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
          ...(propertyIds?.length ? { property_ids: propertyIds } : {})
        }
      }
    );
    return data;
  },

  searchAvailable: async (params: SearchAvailableParams) => {
    const { data } = await boompayClient.get<AvailablePhoneNumber[]>(
      `${PORTAL_BASE}/search_available`,
      { params }
    );
    return data;
  },

  buy: async (payload: BuyPhoneNumberPayload) => {
    const { data } = await boompayClient.post<PhoneNumber>(
      `${PORTAL_BASE}/buy`,
      payload
    );
    return data;
  },

  // Import endpoint lives on comm-service direct API
  // (not exposed by boompay-api as of PR #42).
  importFromTwilio: async (payload: ImportFromTwilioPayload) => {
    const { data } = await inboxClient.post<PhoneNumber>(
      `${COMM_BASE}/import`,
      payload
    );
    return data;
  },

  // Mutation endpoints expect the comm-service UUID (NOT the local DB id).
  update: async (commId: string, payload: UpdatePhoneNumberPayload) => {
    const { data } = await boompayClient.patch<PhoneNumber>(
      `${PORTAL_BASE}/${commId}`,
      payload
    );
    return data;
  },

  remove: async (commId: string) => {
    await boompayClient.delete(`${PORTAL_BASE}/${commId}`);
  },

  setDefault: async (commId: string) => {
    const { data } = await boompayClient.put<PhoneNumber>(
      `${PORTAL_BASE}/${commId}/default`
    );
    return data;
  },

  listPropertyDefaults: async () => {
    const { data } = await inboxClient.get<PropertyPhoneDefault[]>(
      `${COMM_BASE}/properties/defaults`
    );
    return data;
  },

  getPropertyDefault: async (propertyId: number) => {
    const { data } = await inboxClient.get<PropertyPhoneDefault | null>(
      `${COMM_BASE}/properties/${propertyId}/default`
    );
    return data;
  },

  setPropertyDefault: async (propertyId: number, phoneNumberId: string) => {
    const { data } = await inboxClient.put(
      `${COMM_BASE}/properties/${propertyId}/default`,
      { phone_number_id: phoneNumberId }
    );
    return data;
  },

  removePropertyDefault: async (propertyId: number) => {
    await inboxClient.delete(`${COMM_BASE}/properties/${propertyId}/default`);
  },

  // Returns all leasing agents (including drafts). The portal/v1/agents
  // endpoint returns every agent type; we narrow to leasing agents here.
  // `id` is the LeasingAgent record id (required by the PATCH endpoint
  // /portal/v1/agents/leasing_agents/:id), not the parent Agent id.
  listLeasingAgents: async (): Promise<LeasingAgentListItem[]> => {
    const { data } = await boompayClient.get<
      {
        id: number;
        type: string;
        nickname: string;
        leasing_agent: { id: number } | null;
      }[]
    >("/portal/v1/agents");
    return data
      .filter((a) => a.type === "leasing_agent" && a.leasing_agent)
      .map((a) => ({ id: a.leasing_agent!.id, nickname: a.nickname }));
  },

  // PATCH /portal/v1/agents/leasing_agents/:id — used to attach or detach
  // a provisioned phone number from a leasing agent.
  assignLeasingAgentPhone: async (
    leasingAgentId: number,
    provisionedPhoneNumberId: number | null
  ) => {
    const { data } = await boompayClient.patch(
      `/portal/v1/agents/leasing_agents/${leasingAgentId}`,
      { provisioned_phone_number_id: provisionedPhoneNumberId }
    );
    return data;
  },

  updateAssignedProperties: async (
    commId: string,
    payload: AssignedPropertiesPayload
  ) => {
    const { data } = await boompayClient.put<PhoneNumber>(
      `${PORTAL_BASE}/${commId}/properties`,
      payload
    );
    return data;
  }
};
